import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContentPanel } from '@components/ContentPanel';
import { MemberAvatar } from '@components/MemberAvatar';
import { StringValue, ValueList } from '@components/value-list';
import { useAuth } from '@context/AuthProvider';
import {
  displayProfileValue,
  formatProfileDate,
  formatProfileStreet,
} from './helpers';
import { ShootingClearancesPanel } from './components/ShootingClearancesPanel';

export const ProfilePage: React.FC = (): JSX.Element => {
  const { currentUser, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading || !currentUser) {
    return <Box />;
  }

  const { member } = currentUser;
  const roleNames = member.roles.map((role) => role.name).join(', ');

  return (
    <Box>
      <ContentPanel>
        <Stack direction="row" gap="10px" alignItems="center">
          <MemberAvatar
            slug={member.slug}
            name={member.fullName}
            sx={{
              width: '130px',
              height: '130px',
              border: '4px solid #FFF',
            }}
          />
          <Stack alignItems="flex-start" justifyContent="center" gap="6px">
            <Typography
              variant="h1"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: 'text.primary',
              }}
            >
              {member.fullName}
            </Typography>
            {member.roles.length > 0 && (
              <Stack direction="row" spacing="5px" flexWrap="wrap" useFlexGap>
                {member.roles.map((role) => (
                  <Chip
                    key={role.id}
                    label={role.name}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Stack>
      </ContentPanel>

      <ContentPanel
        title={t('members:portal.profile.sections.personal')}
        marginTop
      >
        <ValueList columns={2}>
          <StringValue
            label={t('members:entities.member.firstName')}
            value={member.firstName}
          />
          <StringValue
            label={t('members:entities.member.lastName')}
            value={member.lastName}
          />
          <StringValue
            label={t('members:entities.member.email')}
            value={member.email}
          />
          <StringValue
            label={t('members:entities.member.phone')}
            value={displayProfileValue(member.phone)}
          />
          <StringValue
            label={t('members:entities.member.dateOfBirth')}
            value={formatProfileDate(member.dateOfBirth)}
          />
        </ValueList>
      </ContentPanel>

      <ContentPanel title={t('words.address')} marginTop>
        <ValueList columns={2}>
          <StringValue
            label={t('forms:address.labels.street')}
            value={formatProfileStreet(member.address)}
          />
          <StringValue
            label={t('forms:address.labels.postalCode')}
            value={displayProfileValue(member.address?.postalCode)}
          />
          <StringValue
            label={t('forms:address.labels.city')}
            value={displayProfileValue(member.address?.city)}
          />
        </ValueList>
      </ContentPanel>

      <ContentPanel
        title={t('members:portal.profile.sections.association')}
        marginTop
      >
        <ValueList columns={2}>
          <StringValue
            label={t('words.memberNumber')}
            value={displayProfileValue(member.association?.id)}
          />
          <StringValue
            label={t('words.memberSince')}
            value={formatProfileDate(member.association?.since)}
          />
          <StringValue
            label={t('members:entities.member.roles')}
            value={displayProfileValue(roleNames)}
          />
        </ValueList>
      </ContentPanel>

      <ShootingClearancesPanel
        clearances={member.shootingClearances ?? []}
      />
    </Box>
  );
};
